/**
 * Utilities for date handling and formatting
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Helper function to parse dates with multiple formats
 */
function parseDateSafely(date: string | Date | number) {
  if (typeof date === 'string') {
    // Try to parse different common formats
    const parsed = dayjs(date, ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true);
    return parsed.isValid() ? parsed : dayjs(date);
  }
  return dayjs(date);
}

/**
 * Formats a date using Peru's regional configuration
 * @param date - Date to format (string, Date, or number)
 * @returns Formatted date in dd/MM/yyyy HH:mm:ss format
 * @example
 * formatDate('19/07/2025 21:27:52') // '19/07/2025 21:27:52'
 * formatDate('2025-07-19T21:27:52') // '19/07/2025 21:27:52'
 */
export function formatDate(date: string | Date | number): string {
  return parseDateSafely(date).format('DD/MM/YYYY HH:mm:ss');
}

/**
 * Formats a date with only day, month and year
 * @param date - Date to format (string, Date, or number)
 * @returns Formatted date in dd/MM/yyyy format
 */
export function formatDateShort(date: string | Date | number): string {
  return parseDateSafely(date).format('DD/MM/YYYY');
}

/**
 * Formats a date for display in short format with 2-digit year
 * @param date - Date to format (string, Date, or number)
 * @returns Formatted date in dd/MM/yy format
 */
export function formatDateShortYear(date: string | Date | number): string {
  return parseDateSafely(date).format('DD/MM/YY');
}

/**
 * Verifies if a date is valid
 * @param date - Date to validate
 * @returns true if the date is valid, false otherwise
 */
export function isValidDate(date: any): boolean {
  if (!date) return false;
  return parseDateSafely(date).isValid();
}

/**
 * Converts a date to ISO string format
 * @param date - Date to convert
 * @returns Date in ISO string format or null if invalid
 */
export function toISOString(date: string | Date | number): string | null {
  try {
    const dayjsDate = parseDateSafely(date);
    return dayjsDate.isValid() ? dayjsDate.toISOString() : null;
  } catch {
    return null;
  }
}